import { QRCodeSVG } from 'qrcode.react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { CenteredScreen } from '@/shell/CenteredScreen'
import { Button, EmptyRow, Eyebrow, Pill, Spinner } from '@/ui'
import { formatCountdown, formatPairingCode, pairingDeepLink } from './pairing'
import { useHasSession } from './useSession'
import { usePairing } from './usePairing'
import styles from './PairingScreen.module.css'

const STEPS = [
  'Abra o app Condi e vá em Perfil → Entrar no painel web.',
  'Escaneie o QR code ao lado ou digite o código.',
  'Confirme no app. Este navegador entra sozinho.',
]

/** `pollIntervalMs` is a test seam; production polls every 2s. */
export function PairingScreen({ pollIntervalMs }: { pollIntervalMs?: number }) {
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from ?? '/'
  const hasSession = useHasSession()
  const { state, secondsLeft, retry } = usePairing(
    () => navigate(from, { replace: true }),
    pollIntervalMs,
  )

  if (hasSession) return <Navigate to={from} replace />

  return (
    <CenteredScreen width={860}>
      <div className={styles.layout}>
        <div className={styles.steps}>
          <Eyebrow>Painel de gestão</Eyebrow>
          <h1 className={styles.title}>Entre com o app Condi</h1>
          <p className={styles.lead}>
            Não há senha aqui. Você entra confirmando no app, onde sua conta já está protegida.
          </p>
          <ol className={styles.list}>
            {STEPS.map((text, i) => (
              <li key={text} className={styles.step}>
                <span className={styles.stepNumber}>{i + 1}</span>
                <span>{text}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className={styles.panel}>
          {state.phase === 'creating' && (
            <div className={styles.center}>
              <Spinner size={28} />
            </div>
          )}

          {state.phase === 'waiting' && (
            <>
              <div className={styles.qr}>
                <QRCodeSVG
                  value={pairingDeepLink(state.pairing.pairing_id, state.pairing.code)}
                  size={184}
                  level="M"
                  fgColor="currentColor"
                  bgColor="transparent"
                  role="img"
                  aria-label="QR code para entrar pelo app Condi"
                />
              </div>
              <div className={styles.codeBlock}>
                <Eyebrow>Ou digite o código</Eyebrow>
                <div className={styles.code} data-testid="pairing-code">
                  {formatPairingCode(state.pairing.code)}
                </div>
              </div>
              <Pill tone="pending">Aguardando confirmação no app</Pill>
              <div className={styles.countdown} aria-live="off">
                O código expira em{' '}
                <span className="tabular-nums">{formatCountdown(secondsLeft)}</span>
              </div>
            </>
          )}

          {state.phase === 'denied' && (
            <PanelMessage
              icon="block"
              title="Acesso recusado"
              description="O pedido foi recusado no app. Se foi um engano, gere um novo código."
              onRetry={retry}
            />
          )}
          {state.phase === 'expired' && (
            <PanelMessage
              icon="schedule"
              title="Código expirado"
              description="Por segurança, o código vale por 2 minutos. Gere um novo para continuar."
              onRetry={retry}
            />
          )}
          {state.phase === 'no_access' && (
            <PanelMessage
              icon="lock"
              title="Sem acesso de gestão"
              description="Essa conta não tem permissão de gestão em nenhum condomínio. Peça ao síndico para conceder o acesso."
              onRetry={retry}
            />
          )}
          {state.phase === 'error' && (
            <PanelMessage
              icon="error"
              title="Não foi possível gerar o código"
              description="Verifique sua conexão e tente de novo."
              onRetry={retry}
            />
          )}
        </div>
      </div>
    </CenteredScreen>
  )
}

function PanelMessage({
  icon,
  title,
  description,
  onRetry,
}: {
  icon: 'block' | 'schedule' | 'lock' | 'error'
  title: string
  description: string
  onRetry: () => void
}) {
  return (
    <div className={styles.center}>
      <EmptyRow icon={icon} title={title} description={description} />
      <Button onClick={onRetry}>Gerar novo código</Button>
    </div>
  )
}
